/**
 * Engine answer → contract answer.
 *
 * `answers.ts` builds chart *props* (rows labelled for display, series with
 * colours). The contract carries keys and raw values instead, so this reads
 * each chart back to its keys — which dimension an axis is on follows from the
 * intent that built it — and the client's `presentAnswerChart` relabels and
 * recolours it the same way. The round trip is asserted in
 * `features/deepdive/present.test.ts`.
 */
import type {
  ChatAnswer,
  ChatChart,
  ChatChartCategory,
  ChatCitation,
  ChatMetric,
  DimensionKey,
  SeriesKey,
} from '@/data-sources/contract';
import { toIsoDate } from '@/lib/analytics/dates';

import { X_KEY } from '../engine/aggregate';
import type { Answer, AnswerChart, IntentId } from '../engine/answers';
import { autoBucket, bucketsIn } from '../engine/buckets';
import { dimensionLabels } from '../engine/dimensions';
import { KEYWORD_LABELS } from '../engine/generator';
import { metrics, type MetricId } from '../engine/metrics';

const reverse = (labels: Readonly<Record<string, string>>): Map<string, string> =>
  new Map(Object.entries(labels).map(([value, label]) => [label, value]));

const byLabel = {
  sentiment: reverse(dimensionLabels.sentiment),
  brand: reverse(dimensionLabels.brand),
  topic: reverse(dimensionLabels.topic),
  channel: reverse(dimensionLabels.channel),
  intent: reverse(dimensionLabels.intent),
  keyword: reverse(KEYWORD_LABELS),
  metric: new Map(
    (['reactions', 'comments', 'shares', 'likes'] as MetricId[]).map((id) => [
      metrics[id].label,
      id,
    ]),
  ),
};

const keyOf = (map: Map<string, string>, label: string): string => map.get(label) ?? label;

/** Which dimension a bar chart's axis is on, per intent. */
function barAxis(
  intent: IntentId,
  chart: Extract<AnswerChart, { kind: 'bar' }>,
): (label: string) => ChatChartCategory {
  switch (intent) {
    case 'negativeDrivers':
      return (label) => ({ key: keyOf(byLabel.topic, label), dimension: 'topic' });
    case 'intent':
      return (label) => ({ key: keyOf(byLabel.intent, label), dimension: 'intent' });
    case 'keywords':
      return (label) => ({ key: keyOf(byLabel.keyword, label), label });
    case 'engagement':
      /* "Engagement by type" puts metrics on the axis; its one series is `value`. */
      if (chart.series[0]?.key === 'value')
        return (label) => ({ key: keyOf(byLabel.metric, label) });
      return (label) => ({ key: keyOf(byLabel.channel, label), dimension: 'channel' });
    default:
      return (label) => ({ key: keyOf(byLabel.channel, label), dimension: 'channel' });
  }
}

/** A split series is a dimension option; a single one is a metric (or `value`). */
function seriesKey(key: string, split: DimensionKey | undefined): SeriesKey {
  return split ? { key, dimension: split } : { key };
}

export function toContractChart(answer: Answer): ChatChart | undefined {
  const { chart, intent } = answer;
  if (!chart) return undefined;
  const metric = chart.metric as ChatMetric;

  switch (chart.kind) {
    case 'donut': {
      const dimension: DimensionKey = intent === 'compare' ? 'brand' : 'sentiment';
      return {
        kind: 'donut',
        metric,
        center: intent === 'compare' ? 'own-share' : 'total',
        slices: chart.rows.map((row) => ({
          key: keyOf(byLabel[dimension], String(row[chart.nameKey])),
          dimension,
          value: Number(row[chart.valueKey] ?? 0),
        })),
      };
    }
    case 'bar': {
      const category = barAxis(intent, chart);
      const split: DimensionKey | undefined =
        intent === 'negativeDrivers' ? 'sentiment' : undefined;
      return {
        kind: 'bar',
        metric,
        orientation: chart.orientation,
        ...(chart.stacked !== undefined && { stacked: chart.stacked }),
        ...(chart.normalize !== undefined && { normalize: chart.normalize }),
        categories: chart.rows.map((row) => category(String(row[X_KEY]))),
        series: chart.series.map((s) => ({
          ...seriesKey(s.key, split),
          values: chart.rows.map((row) => Number(row[s.key] ?? 0)),
        })),
      };
    }
    case 'line': {
      /* Every line in `answers.ts` is an `aggregate` over the answer's period with the auto bucket. */
      const { from, to } = answer.period;
      const bucket = autoBucket(from, to);
      const split: DimensionKey | undefined = intent === 'channels' ? 'channel' : undefined;
      return {
        kind: 'line',
        metric,
        series: {
          bucket,
          x: bucketsIn(from, to, bucket).map((start) => toIsoDate(start)),
          series: chart.series.map((s) => ({
            ...seriesKey(s.key, split),
            values: chart.rows.map((row) => Number(row[s.key] ?? 0)),
          })),
        },
      };
    }
  }
}

/** `#brand-health` (a prototype hash route) → `/brand-health`; Overview is the index. */
function citationOf(citation: Answer['citations'][number]): ChatCitation {
  const path = citation.url.replace(/^#/, '/');
  return {
    id: citation.id,
    index: citation.index,
    title: citation.title,
    url: path === '/overview' ? '/' : path,
    ...(citation.host !== undefined && { host: citation.host }),
  };
}

export function toContractAnswer(answer: Answer): ChatAnswer {
  const chart = toContractChart(answer);
  return {
    text: answer.text,
    reasoning: answer.reasoning.map((step) => ({ id: step.id, content: step.content })),
    ...(chart && { chart }),
    citations: answer.citations.map(citationOf),
    followUps: answer.followUps.map((prompt) => ({ id: prompt.id, label: prompt.label })),
  };
}
